import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';

export default function SalesDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [successMessage, setSuccessMessage] = useState(null);
  const [counts, setCounts] = useState({ pending: 0, confirmed: 0, rejected: 0 });
  const [countsLoading, setCountsLoading] = useState(true);
  const [revenue, setRevenue] = useState({ totalRevenue: 0, summary: { successfulCount: 0, failedCount: 0 } });
  const [revenueLoading, setRevenueLoading] = useState(true);
  const displayName = user?.name?.split(' ')[0] || 'User';

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location.state?.message, location.pathname]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await apiService.orders.getSalesOrderCounts();
        if (!cancelled && res.data) setCounts(res.data);
      } catch {
        if (!cancelled) setCounts({ pending: 0, confirmed: 0, rejected: 0 });
      } finally {
        if (!cancelled) setCountsLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadRevenue() {
      try {
        const res = await apiService.orders.getSalesOrdersRevenue();
        if (cancelled) return;
        setRevenue({
          totalRevenue: Number(res.data?.totalRevenue || 0),
          summary: {
            successfulCount: Number(res.data?.summary?.successfulCount || 0),
            failedCount: Number(res.data?.summary?.failedCount || 0),
          }
        });
      } catch {
        if (!cancelled) {
          setRevenue({ totalRevenue: 0, summary: { successfulCount: 0, failedCount: 0 } });
        }
      } finally {
        if (!cancelled) setRevenueLoading(false);
      }
    }
    loadRevenue();
    return () => { cancelled = true; };
  }, []);

  const averageBucket =
    revenue.summary.successfulCount > 0
      ? revenue.totalRevenue / revenue.summary.successfulCount
      : 0;

  return (
    <div className="p-3 sm:p-4 md:p-6 pb-6 sm:pb-8">
      <div className="bg-white rounded-lg shadow-md border-l-4 border-blue-500 p-3 sm:p-5 md:p-6">
        {successMessage && (
          <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-800 rounded-lg text-sm sm:text-base">
            {successMessage}
          </div>
        )}
        <h1 className="text-xl sm:text-2xl font-bold text-gray-700 mb-1">
          Hello, {displayName}!
        </h1>
        <p className="text-sm sm:text-base text-gray-600 mb-6">Manage your orders.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-8">
          <button
            type="button"
            onClick={() => navigate('/sales/orders')}
            className="min-h-[44px] sm:min-h-0 bg-orange-200 hover:bg-orange-300 active:bg-orange-400 text-gray-800 rounded-xl py-4 px-4 sm:py-4 sm:px-3 flex flex-col items-center justify-center border border-orange-300 transition-colors touch-manipulation"
          >
            <span className="text-2xl sm:text-2xl mb-2">🕐</span>
            <span className="text-sm sm:text-sm font-semibold">Pending Orders</span>
            {!countsLoading && (
              <span className="text-xs mt-1 text-gray-600 text-center">
                Pending: {counts.pending} · Confirmed: {counts.confirmed} · Rejected: {counts.rejected}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate('/sales/menu')}
            className="min-h-[44px] sm:min-h-0 bg-green-200 hover:bg-green-300 active:bg-green-400 text-gray-800 rounded-xl py-4 px-4 sm:py-4 sm:px-3 flex flex-col items-center justify-center border border-green-300 transition-colors touch-manipulation"
          >
            <span className="text-2xl sm:text-2xl mb-2">➕</span>
            <span className="text-sm sm:text-sm font-semibold">New Order</span>
          </button>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="text-sm font-semibold text-gray-700">Revenue (Paid)</div>
            <button
              type="button"
              onClick={() => navigate('/sales/revenue')}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              View revenue
            </button>
          </div>

          {revenueLoading ? (
            <div className="py-6 text-center text-gray-500">Loading…</div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                <div className="bg-white border rounded-lg p-3">
                  <div className="text-xs text-gray-500">Successful orders (Paid)</div>
                  <div className="text-lg sm:text-xl font-bold text-gray-800 tabular-nums">
                    {revenue.summary.successfulCount}
                  </div>
                </div>
                <div className="bg-white border rounded-lg p-3">
                  <div className="text-xs text-gray-500">Failed orders</div>
                  <div className="text-lg sm:text-xl font-bold text-gray-800 tabular-nums">
                    {revenue.summary.failedCount}
                  </div>
                </div>
                <div className="bg-white border rounded-lg p-3 col-span-2 lg:col-span-1">
                  <div className="text-xs text-gray-500">Average bucket</div>
                  <div className="text-lg sm:text-xl font-bold text-gray-800 tabular-nums">
                    SAR {Number(averageBucket).toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="mt-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="text-sm text-gray-600">Total revenue</div>
                <div className="text-xl sm:text-2xl font-bold text-green-800 tabular-nums">
                  SAR {Number(revenue.totalRevenue).toFixed(2)}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
